import {
  Badge,
  Container,
  KeyValuePairs,
  KeyValuePairsProps,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { AccountingEvent, AccountingEventsGroup } from "../../types/events";
import {
  getEMPSubsOrchestrationPlatform,
  getFulfillmentState,
  getPaymentState,
  getRefundState,
  hasSettlementDetails,
  isTvodAccountingEvent,
} from "../../helpers/AccountingEventsUtils";
import { screamingSnakeToTitleCase } from "../../helpers/dbdUtils";
import DigitalOrderSourceLink from "../common/DigitalOrderSourceLink";
import { PromotionStatus } from "../common/PromotionStatus";
import Item = KeyValuePairsProps.Item;

interface AccountingEventsSummarySectionProps {
  accountingGroup: AccountingEventsGroup;
  event: AccountingEvent;
}

export function AccountingEventsSummarySection({
  accountingGroup,
  event,
}: AccountingEventsSummarySectionProps) {
  const fulfillmentState = getFulfillmentState(event);
  const paymentState = getPaymentState(event);
  const refundState = getRefundState(event);

  const items: Item[] = [
    {
      label: "Created",
      value: (
        <SpaceBetween direction="horizontal" size="xs">
          {accountingGroup.creationDate
            ? new Date(accountingGroup.creationDate).toLocaleString()
            : "Unknown"}
          {accountingGroup.isRedrive && (
            <Badge color="grey">Manual Redrive</Badge>
          )}
        </SpaceBetween>
      ),
    },
    ...(!isTvodAccountingEvent(event)
      ? [
          {
            label: "Subs ID",
            value: (
              <SpaceBetween direction="horizontal" size="xs">
                {`${event.subscriptionId}:${event.subscriptionPeriod}`}
              </SpaceBetween>
            ),
          },
        ]
      : []),
    {
      label: "Fulfillment Type",
      value: screamingSnakeToTitleCase(event.fulfillmentType || "IMMEDIATE"),
    },
    {
      label: "Fulfillment Status",
      value: (
        <StatusIndicator type={fulfillmentState.status}>
          {fulfillmentState.text}{" "}
          {event.cancellationReason && `- (${event.cancellationReason})`}
        </StatusIndicator>
      ),
    },
    {
      label: "Payment Status",
      value: (
        <StatusIndicator type={paymentState.status}>
          {paymentState.text}
        </StatusIndicator>
      ),
    },
    {
      label: "Promotions",
      value: event ? (
        <PromotionStatus
          promotionId={event.discountsOnRevenue?.[0]?.promotionId}
        />
      ) : (
        <StatusIndicator type="stopped">No Product Data</StatusIndicator>
      ),
    },
    {
      label: "Refund Status",
      value: event ? (
        <StatusIndicator type={refundState.status}>
          {refundState.text}
        </StatusIndicator>
      ) : (
        <StatusIndicator type="stopped">No Product Data</StatusIndicator>
      ),
    },
    {
      label: "Settlement Details",
      value: (
        <StatusIndicator
          type={
            hasSettlementDetails(accountingGroup.accountingEvents)
              ? "success"
              : "stopped"
          }
        >
          {hasSettlementDetails(accountingGroup.accountingEvents)
            ? "Has Settlement Details"
            : "No Settlement Details"}
        </StatusIndicator>
      ),
    },
    {
      label: "Digital Order Source",
      value: (
        <DigitalOrderSourceLink
          source={event.digitalOrderSource}
          event={event}
        />
      ),
    },
  ];

  if (event.isEmpOrder && !isTvodAccountingEvent(event)) {
    items.push({
      label: "EMP Subscription Orchestration Platform",
      value: getEMPSubsOrchestrationPlatform(event),
    });
  }

  return (
    <Container>
      <KeyValuePairs columns={2} items={items} />
    </Container>
  );
}
